import algoliasearch from "algoliasearch/lite";
import moment from "moment";

import { Maybe, SanityNews, SanityRegion } from "@graphql-types";
import { Region } from "./types";

export const isBrowser = () => typeof window !== "undefined";

export function arraysEquality(array1: any[], array2: any[]) {
  return array1.length === array2.length && array1.every((value, index) => value === array2[index]);
}

export function objectEquality(object1: any, object2: any) {
  return Object.keys(object1).every(key => object1[key] === object2[key]);
}

export function splitArrayIntoChunks(array: any[], split: number) {
  const chunks = array.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / split);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }

    resultArray[chunkIndex].push(item);

    return resultArray;
  }, []);

  return chunks;
}

export function setBaseUrl(slug?: string, iban?: string, sanityType?: string) {
  const baseIban = process.env.GATSBY_DEFAULT_IBAN_REGION;
  const baseUrl = sanityType ? "" : process.env.GATSBY_BASE_URL;

  const path = `${slug}`;

  if (slug == null && iban === baseIban) {
    return baseUrl;
  }

  if (slug === "/" && iban === baseIban) {
    return `/`;
  }

  if (slug === "/" && iban !== baseIban) {
    return `${baseUrl}/${iban}`;
  }

  if (slug == null) {
    return `${baseUrl}/${iban}`;
  }

  if (iban === baseIban) {
    return `${baseUrl}/${path}`;
  }

  if (iban == null) {
    return `${baseUrl}/${path}`;
  }

  return `${baseUrl}/${iban}/${path}`;
}

export const checkClickTarget = (
  e: Event,
  className: string,
  trueCallBack: () => void,
  falseCallBack?: () => void,
) => {
  if (e == null) return null;

  const target = e.target as Element;
  if (target && target.classList.contains(className)) {
    trueCallBack();
    return;
  }

  falseCallBack && falseCallBack();
  return;
};

export function filterItemWithAndMethod(
  valueOne: Maybe<string> | undefined,
  valueTwo: Maybe<string> | undefined,
  categoryOne: Maybe<string> | undefined,
  categoryTwo: Maybe<string> | undefined,
) {
  if (valueOne == null && valueTwo == null) return null;
  const categoryOneMatch = valueOne === categoryOne;
  const categoryTwoMatch = valueTwo === categoryTwo;

  if (categoryOne == null || categoryOne === "All") {
    if (categoryTwoMatch) return true;
  }

  if (categoryTwo == null || categoryTwo === "All") {
    if (categoryOne == null || categoryOne === "All") return true;
    if (categoryOneMatch) return true;
    return null;
  }

  if (categoryOneMatch && categoryTwoMatch) return true;

  return null;
}

export const sortArticles = (articles: SanityNews[], sortValue: string) => {
  if (sortValue === "Earliest") {
    const sortedArray = articles.sort((a, b) =>
      moment(a.date, "DD-MM-YYYY").isBefore(moment(b.date, "DD-MM-YYYY")) ? -1 : 1,
    );
    return sortedArray;
  }
  if (sortValue === "Latest") {
    const sortedArray = articles.sort((a, b) =>
      moment(a.date, "DD-MM-YYYY").isBefore(moment(b.date, "DD-MM-YYYY")) ? 1 : -1,
    );
    return sortedArray;
  }
  if (sortValue === "Alphabetical (A-Z)") {
    return articles.sort((a, b) => {
      if (a.title == null || b.title == null) return 1;
      return a.title.localeCompare(b.title);
    });
  }
  if (sortValue === "Alphabetical (Z-A)") {
    const sortedArray = articles.sort((a, b) => {
      if (a.title == null || b.title == null) return -1;
      return a.title.localeCompare(b.title);
    });
    return sortedArray.reverse();
  }
  return articles;
};

// ALGOLIA SEARCH CLIENT CONFIG

const algoliaClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID as string,
  process.env.GATSBY_ALGOLIA_SEARCH_ONLY_KEY as string,
);

export const searchClient = {
  search(requests: any) {
    const shouldSearch = requests.some(({ params: { query } }) => query !== "");
    const facetFilter = requests.some(params => params.params["facetFilters"] !== undefined);

    if (shouldSearch || facetFilter) {
      return algoliaClient.search(requests);
    }

    return Promise.resolve({
      results: [{ hits: [] }],
    });
  },
  searchForFacetValues: algoliaClient.searchForFacetValues,
};

export function linkHomeAttributes(region: Maybe<SanityRegion | Region> | undefined) {
  const iban = region ? region.iban : process.env.GATSBY_DEFAULT_IBAN_REGION;
  const linkHomeAttr = {
    internalLink: {
      _type: "page",
      slug: {
        current: "/",
      },
      region: {
        iban: iban,
      },
      internal: {
        type: "page",
      },
    },
  };
  return { linkHomeAttr };
}

export const formatTitle = (titleData: string[]) => {
  let result = "";
  if (titleData && typeof titleData[0] === "string") {
    result = titleData[0]
      .toLowerCase()
      .replace(/[^a-z\s]/g, "") // Remove special characters and numbers except spaces
      .trim() // Remove spaces from the beginning and end of the string
      .replace(/\s+/g, "-"); // Replace remaining spaces with hyphens
  }
  return result;
};

export const removeSpacesAndCommasFromArrayOfId = (arr: string[]): string => {
  const combinedString = arr
    .join(" ")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]/g, "");
  return `#${combinedString}`;
};
